import { useEffect, useRef, useState } from "react";
import apiInstance from "../../apiInstance";
import PopupModal from "../Popups/PopupModal";
import { useNavigate } from "react-router-dom";

const getStatus = (email) => {
    if (email.clicked) return "Clicked";
    if (email.bounced) return "Bounced";
    if (email.delivered) return "Delivered";
    return "Sent";
};

const getFollowUp = (email) => {
    if (email.followup3SentAt) return "Three";
    if (email.followup2SentAt) return "Two";
    if (email.followup1SentAt) return "One";
    return "No";
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const DevadminColdEmails = () => {
    const [emails, setEmails] = useState([]);
    const [totalEmails, setTotalEmails] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [emailsPerPage, setEmailsPerPage] = useState(50);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [bulkAction, setBulkAction] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        status: "",
        replied: "",
        complaint: "",
        unsubscribed: "",
    });
    const [error, setError] = useState("");
    const popupRef = useRef();
    const navigate = useNavigate();

    const deleteEmailHandler = (emailId) => {
        setEmails((prev) => prev.filter((item) => item._id !== emailId));
    };

    const updateEmailHandler = (email) => {
        setEmails((prev) =>
            prev.map((item) =>
                item._id === email._id ? { ...item, ...email } : item
            )
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedEmails(emails.map((item) => item._id));
        } else {
            setSelectedEmails([]);
        }
    };

    const handleSelect = (e, emailId) => {
        setSelectedEmails((prev) => {
            if (prev.includes(emailId)) {
                return prev.filter((id) => id !== emailId);
            } else {
                return [...prev, emailId];
            }
        });
    };

    const handleBulkAction = async () => {
        if (bulkAction === "delete") {
            for (const emailId of selectedEmails) {
                try {
                    await apiInstance.delete(`/devadmin/cold-email/${emailId}`);
                    setEmails((prev) =>
                        prev.filter((item) => item._id !== emailId)
                    );
                } catch (err) {
                    console.error(`Error deleting email:`, err);
                    setError(`Error deleting email ${emailId}.`);
                }
            }
            setSelectedEmails([]);
            setReload((prev) => prev + 1);
        }
    };

    const handleCurrentPageChange = (p) => {
        const totalPages = Math.ceil(totalEmails / emailsPerPage);
        const value = Math.min(totalPages, Math.max(1, p));
        setCurrentPage(value);
        setSelectedEmails([]);
    };

    const renderPaginationControls = () => {
        let totalPages = 0;
        let startItem = 0;
        let endItem = 0;
        if (totalEmails > 0) {
            totalPages = Math.ceil(totalEmails / emailsPerPage);
            startItem = (currentPage - 1) * emailsPerPage + 1;
            endItem = Math.min(currentPage * emailsPerPage, totalEmails);
        }

        return (
            <div className="mjsdev-webs-pagination-wrapper">
                Per Page
                <select
                    value={emailsPerPage}
                    onChange={(e) => {
                        setEmailsPerPage(Number(e.target.value));
                        handleCurrentPageChange(1);
                    }}
                >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                </select>
                <span>
                    Showing {startItem}-{endItem} of {totalEmails}
                </span>
                <button
                    onClick={() => handleCurrentPageChange(1)}
                    disabled={currentPage === 1}
                >
                    &laquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    &lsaquo;
                </button>
                <input
                    type="number"
                    value={currentPage}
                    onChange={(e) =>
                        handleCurrentPageChange(Number(e.target.value))
                    }
                />
                <span>of {totalPages}</span>
                <button
                    onClick={() => handleCurrentPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    &rsaquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    &raquo;
                </button>
            </div>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search || "");
        }, 400);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const fetchEmails = async () => {
            apiInstance
                .get(`/devadmin/cold-email`, {
                    params: {
                        page: currentPage,
                        limit: emailsPerPage,
                        search: debouncedSearch,
                        ...filters,
                    },
                })
                .then((response) => {
                    setEmails(response.data.emails || []);
                    setTotalEmails(response.data.pagination?.total || 0);
                    setSelectedEmails([]);
                })
                .catch((err) => {
                    console.error("Error fetching cold emails:", err);
                    setError("Failed to load cold emails.");
                });
        };
        fetchEmails();
    }, [currentPage, emailsPerPage, reload, filters, debouncedSearch]);

    return (
        <>
            <div className="mjsdev-main">
                <div className="mjsdev-webs-header">
                    <h1></h1>
                </div>

                <div className="mjsdev-webs-toolbar">
                    <div className="mjsdev-webs-bulk_actions">
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            className="mjs-btn"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || selectedEmails.length === 0}
                        >
                            Apply
                        </button>
                    </div>

                    <div className="mjsdev-webs-filters">
                        <div className="mjsdev-search-wrapper">
                            <input
                                style={{width: "150px"}}
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {search && (
                                <i
                                    className="fa-solid fa-times"
                                    onClick={() => setSearch("")}
                                    title="Clear search"
                                ></i>
                            )}
                        </div>
                        <select
                            value={filters.status}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    status: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Status</option>
                            <option value="clicked">Clicked</option>
                            <option value="delivered">Delivered</option>
                            <option value="bounced">Bounced</option>
                            <option value="sent">Sent</option>
                        </select>
                        <select
                            value={filters.replied}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    replied: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Replied</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                        <select
                            value={filters.complaint}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    complaint: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Complaints</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                        <select
                            value={filters.unsubscribed}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    unsubscribed: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">Unsubscribed</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                        </select>
                    </div>

                    <div className="mjsdev-webs-pgn-top">
                        {renderPaginationControls()}
                    </div>
                </div>

                {error && <p className="mjs-error">{error}</p>}

                <table className="mjsdev-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        emails.length > 0 &&
                                        selectedEmails.length === emails.length
                                    }
                                />
                            </th>
                            <th>To</th>
                            <th>Site Name</th>
                            <th>Status</th>
                            <th>Follow Up</th>
                            <th>Replied</th>
                            <th>Complaint</th>
                            <th>Unsubscribed</th>
                            <th>Sent</th>
                            <th></th>
                        </tr>
                    </thead>
                    {emails.length > 0 ? (
                        <tbody>
                            {emails.map((email) => (
                                <tr key={email._id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            onChange={(e) =>
                                                handleSelect(e, email._id)
                                            }
                                            checked={selectedEmails.includes(
                                                email._id
                                            )}
                                        />
                                    </td>
                                    <td>{email.email || "-"}</td>
                                    <td>
                                        <a
                                            className="mjsdev-link"
                                            href={email.siteUrl}
                                            target="_blank"
                                        >
                                            {email.business_name || "-"}
                                        </a>
                                    </td>
                                    <td>{getStatus(email)}</td>
                                    <td>{getFollowUp(email)}</td>
                                    <td>{email.replied ? "Yes" : "No"}</td>
                                    <td>{email.complaint ? "Yes" : "No"}</td>
                                    <td>{email.unsubscribed ? "Yes" : "No"}</td>
                                    <td>{formatDate(email.createdAt)}</td>
                                    <td className="mjsdev-table-actions">
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "editColdEmail",
                                                        { email }
                                                    );
                                                }
                                            }}
                                            title="Edit"
                                            className="fa-solid fa-pen-to-square"
                                        ></i>
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "deleteColdEmail",
                                                        { emailId: email._id }
                                                    );
                                                }
                                            }}
                                            title="Delete"
                                            className="fa-solid fa-trash"
                                        ></i>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : (
                        <p className="mjsdev-msg">No cold emails found.</p>
                    )}
                </table>

                <div className="mjsdev-webs-pgn-bottom">
                    {renderPaginationControls()}
                </div>
            </div>
            <PopupModal
                ref={popupRef}
                deleteColdEmail={deleteEmailHandler}
                updateColdEmail={updateEmailHandler}
            />
        </>
    );
};

export default DevadminColdEmails;
